import React from "react";
import { Link } from "react-router-dom";
import HeroSlider from "../components/HeroSlider";
import Chapter from "../components/Chapter";
import FullBleed from "../components/FullBleed";
import EditorialMarquee from "../components/Marquee";
import { FadeUp, MaskLineScroll } from "../components/Reveal";
import SEO from "../components/SEO";
import { BRAND, IMAGES, TID, PARTNER_LOGOS } from "../lib/brand";

const services = [
  { t: "Insurance Collision Repair", desc: "Full-service claim workflows and documentation for daily-driven vehicles." },
  { t: "Custom Paint & Color Change", desc: "Complete-vehicle refinishing, multi-stage color, specialty applications." },
  { t: "JDM & Imported Vehicles", desc: "Rare panels, discontinued parts, chassis knowledge, and cultural care.", img: IMAGES.jdmSide },
  { t: "Race, Track & Drift Bodywork", desc: "Event-damage recovery, cosmetic prep, lightweight & fiberglass panels.", img: IMAGES.drift },
  { t: "Structural & Frame Assessment", desc: "Assessment planning for modified, imported, and non-standard structures." },
  { t: "Panel Repair & Replacement", desc: "OEM, aftermarket, and specialty panels with correct alignment protocols." },
  { t: "Paint Correction & Refinishing", desc: "Spot-blend to multi-panel refinishing and clear-coat correction." },
  { t: "Specialty Fabrication", desc: "Carbon fiber, fiberglass, and one-off body components on request." },
];

const process = [
  { n: "01", t: "Send the details", d: "Call, text, or submit the estimate form with photos and vehicle information." },
  { n: "02", t: "Assess the project", d: "We review damage, desired outcome, parts, and insurance involvement." },
  { n: "03", t: "Define the repair", d: "Scope, process, estimate, scheduling, and required approvals." },
  { n: "04", t: "Repair, refinish, inspect", d: "Work is completed, inspected, documented, and prepared for delivery." },
];

const partners = ["Missile Works", "RevHard", "MoFab", "Larimer Street Garage"];

export default function Home() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "AutoBodyShop",
    name: BRAND.name,
    telephone: BRAND.phoneDisplay,
    email: BRAND.email,
    url: `https://${BRAND.domain}`,
    areaServed: { "@type": "AdministrativeArea", name: `${BRAND.city} Metro, ${BRAND.state}` },
    sameAs: [BRAND.instagramUrl],
    slogan: BRAND.tagline,
  };

  return (
    <>
      <SEO
        title="Denver Auto Body, Collision Repair & Custom Paint | ProForm Paint & Body"
        description="ProForm provides collision repair, insurance coordination, custom paint, JDM bodywork, refinishing, race-car repair and specialty automotive services in the Denver, Colorado area."
        canonical={`https://${BRAND.domain}/`}
        schema={schema}
      />
      <HeroSlider />

      {/* Slow editorial marquee */}
      <EditorialMarquee items={["PROFORM PAINT & BODY", "DENVER · COLORADO", "BUILT DIFFERENT", "FINISHED RIGHT"]} />

      {/* Chapter 01 — Positioning · FULL-BLEED */}
      <div id="manifesto" />
      <FullBleed
        image={IMAGES.specialist}
        alt="Rare Japanese performance vehicle in a dark industrial workshop — temporary atmospheric imagery, not a documented ProForm project"
        side="left"
        redAccent
        testid={TID.home.manifesto}
      >
        <div className="label mb-4">— Chapter 01 · Positioning</div>
        <h2 className="font-display uppercase text-4xl md:text-6xl leading-[0.9] tracking-tight text-chrome-100">
          <MaskLineScroll>When other shops</MaskLineScroll>
          <MaskLineScroll delay={0.06}>won&apos;t touch it,</MaskLineScroll>
          <MaskLineScroll delay={0.12}>Denver&apos;s performance</MaskLineScroll>
          <MaskLineScroll delay={0.18}>community calls <span className="text-proform-red">ProForm</span>.</MaskLineScroll>
        </h2>
        <div className="mt-8 md:mt-10 text-chrome-100/85 text-base md:text-lg leading-relaxed max-w-xl drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]">
          <p>
            Rare imports, discontinued parts, modified structures, aftermarket panels, and unusual paint systems demand more than production-line repair procedures. ProForm combines traditional paint-and-body craftsmanship with practical knowledge of JDM, drift, race, and performance vehicles.
          </p>
          <p className="mt-6 text-chrome-100">
            <span className="text-proform-red font-medium">The same precision applies to your daily driver.</span> Ordinary customers get the same standard of preparation, alignment, and finish quality — with clear insurance communication.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/services" className="btn">See services →</Link>
            <Link to="/insurance" className="btn">Insurance workflow →</Link>
          </div>
        </div>
      </FullBleed>

      {/* Insurance Repair Trust — concise, credible, no unsupported claims */}
      <section data-testid="section-insurance-trust" className="relative py-24 md:py-32 border-t border-ink-700">
        <div className="wrap grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="label mb-4">— Chapter 02 · Insurance</div>
            <h2 className="font-display uppercase text-4xl md:text-6xl leading-[0.95] tracking-tight">
              <MaskLineScroll>Insurance repair,</MaskLineScroll>
              <MaskLineScroll delay={0.06}>without the <span className="text-proform-red">runaround</span>.</MaskLineScroll>
            </h2>
            <p className="mt-6 text-chrome-300 leading-relaxed max-w-md">
              ProForm works with customers and insurance carriers throughout the repair process. We help document the visible damage, communicate repair requirements, review supplements when additional damage is discovered, and keep the repair plan clear.
            </p>
            <p className="mt-4 label text-chrome-100">We work with all major insurance companies.</p>
            <p className="mt-1 label text-chrome-300 normal-case tracking-wider">You have the right to choose your repair facility subject to applicable policy terms.</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/estimate" className="btn btn-primary">Start a photo assessment →</Link>
              <Link to="/insurance" className="btn">Insurance details</Link>
            </div>
          </div>
          <div className="md:col-span-7">
            <ol className="grid gap-4 md:gap-5">
              {[
                { n: "01", t: "Damage Documentation", d: "Photos, inspection details, and repair requirements are organized clearly." },
                { n: "02", t: "Claim Communication", d: "ProForm works with the customer and insurer throughout the repair process." },
                { n: "03", t: "Supplement Support", d: "Additional documented damage can be submitted when discovered during repair." },
              ].map((p) => (
                <li key={p.n} className="card p-6 md:p-8 flex items-start gap-6">
                  <div className="font-display text-5xl md:text-6xl text-proform-red leading-none shrink-0">{p.n}</div>
                  <div>
                    <div className="font-display uppercase text-xl md:text-2xl tracking-tight">{p.t}</div>
                    <p className="mt-2 text-chrome-300 text-sm md:text-base leading-relaxed">{p.d}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
        <div className="wrap mt-10 label normal-case tracking-wider text-chrome-300">
          Insurance companies do not own or operate ProForm. Photo reviews are preliminary; hidden damage or supplements may affect the final repair plan and price.
        </div>
      </section>

      {/* Chapter 03 — Two Sides */}
      <section data-testid={TID.home.split} className="relative border-y border-ink-700">
        <div className="grid md:grid-cols-2">
          {/* Specialty */}
          <div className="relative overflow-hidden group border-b md:border-b-0 md:border-r border-ink-700">
            <div className="absolute inset-0 -z-10">
              <img src={IMAGES.jdmSide} alt="Imported JDM specialty vehicle" className="w-full h-full object-cover opacity-40 group-hover:opacity-60 group-hover:scale-105 transition-all duration-[1200ms]" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink-950 via-ink-950/70 to-transparent" />
            </div>
            <div className="p-8 md:p-16 min-h-[70vh] flex flex-col justify-end">
              <div className="label mb-4">— Specialty & Performance</div>
              <h3 className="font-display uppercase text-4xl md:text-6xl leading-[0.95] tracking-tight">
                <MaskLineScroll>Rare, imported,</MaskLineScroll>
                <MaskLineScroll delay={0.05}>modified, <span className="text-proform-red">built</span>.</MaskLineScroll>
              </h3>
              <ul className="mt-6 grid grid-cols-2 gap-x-8 gap-y-2 text-chrome-300 text-sm max-w-md">
                {["JDM vehicles", "Imported vehicles", "Drift cars", "Race & track cars", "Restorations", "Custom builds", "Color changes", "Discontinued parts"].map((s) => (
                  <li key={s} className="border-t border-ink-700 pt-2">{s}</li>
                ))}
              </ul>
              <Link to="/services#specialty" className="btn mt-8 self-start">Specialty services →</Link>
            </div>
          </div>

          {/* Collision */}
          <div className="relative overflow-hidden group">
            <div className="absolute inset-0 -z-10">
              <img src={IMAGES.paintReflection} alt="Deep-black paint reflection detail" className="w-full h-full object-cover opacity-40 group-hover:opacity-60 group-hover:scale-105 transition-all duration-[1200ms]" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink-950 via-ink-950/70 to-transparent" />
            </div>
            <div className="p-8 md:p-16 min-h-[70vh] flex flex-col justify-end">
              <div className="label mb-4">— Collision & Insurance</div>
              <h3 className="font-display uppercase text-4xl md:text-6xl leading-[0.95] tracking-tight">
                <MaskLineScroll>Daily-driven</MaskLineScroll>
                <MaskLineScroll delay={0.05}>vehicles, <span className="text-proform-red">repaired right</span>.</MaskLineScroll>
              </h3>
              <ul className="mt-6 grid grid-cols-2 gap-x-8 gap-y-2 text-chrome-300 text-sm max-w-md">
                {["Insurance claims", "Body-panel repair", "Body-panel replacement", "Paint matching", "Structural assessment", "Refinishing", "Insurance coordination", "Quality control"].map((s) => (
                  <li key={s} className="border-t border-ink-700 pt-2">{s}</li>
                ))}
              </ul>
              <Link to="/insurance" className="btn mt-8 self-start">Insurance repairs →</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Chapter 03 — Craft · FULL-BLEED */}
      <FullBleed
        image={IMAGES.craft}
        alt="Freshly refinished deep-black quarter panel with clear-coat depth — temporary atmospheric imagery"
        side="right"
        scrim="right"
        objectPosition="50% 0%"
        testid={TID.home.craft}
      >
        <div className="label mb-4">— Chapter 03 · The Craft</div>
        <h2 className="font-display uppercase text-4xl md:text-6xl leading-[0.9] tracking-tight text-chrome-100">
          <MaskLineScroll>The finish is what</MaskLineScroll>
          <MaskLineScroll delay={0.06}>everyone sees.</MaskLineScroll>
          <MaskLineScroll delay={0.12}>The prep is what</MaskLineScroll>
          <MaskLineScroll delay={0.18}><span className="text-proform-red">lasts</span>.</MaskLineScroll>
        </h2>
        <p className="mt-8 md:mt-10 text-chrome-100/85 text-base md:text-lg leading-relaxed max-w-xl drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]">
          Every project moves through the same disciplined path: preparation, panel alignment, masking, surface correction, application, clear coat, reassembly, and final inspection. Nothing is rushed to the paint booth. Nothing leaves without a walk-around.
        </p>
        <div className="mt-8 grid grid-cols-2 gap-2 max-w-md">
          {["Prep", "Panel Alignment", "Masking", "Surface Correction", "Application", "Clear Coat", "Reassembly", "Inspection"].map((s, i) => (
            <div key={s} className="flex items-center gap-2 border-t border-chrome-100/25 pt-2 text-chrome-100/90 text-sm">
              <span className="font-mono text-[10px] tracking-[0.24em] text-proform-red">0{i + 1}</span>
              <span>{s}</span>
            </div>
          ))}
        </div>
      </FullBleed>

      {/* Services grid */}
      <section data-testid={TID.home.services} className="relative py-24 md:py-32 border-t border-ink-700">
        <div className="wrap">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5 mb-14 md:mb-16">
            <div>
              <div className="label mb-3">— Chapter 04 · Services</div>
              <h2 className="font-display uppercase text-4xl md:text-6xl leading-[0.95] tracking-tight">
                <MaskLineScroll>What we do,</MaskLineScroll>
                <MaskLineScroll delay={0.08}>quietly and <span className="text-proform-red">correctly</span>.</MaskLineScroll>
              </h2>
            </div>
            <Link to="/services" className="btn self-start md:self-auto shrink-0">Full services →</Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {services.map((s, i) => (
              <FadeUp key={s.t} delay={i * 0.04}>
                <Link
                  to="/services"
                  className="group relative flex flex-col h-full min-h-[220px] md:min-h-[270px] card p-7 md:p-8 overflow-hidden transition-colors duration-300 hover:border-proform-red hover:bg-ink-800"
                >
                  {s.img && (
                    <div className="absolute inset-0 opacity-[0.14] group-hover:opacity-[0.22] transition-opacity duration-500 pointer-events-none">
                      <img src={s.img} alt="" className="w-full h-full object-cover" loading="lazy" />
                      <div className="absolute inset-0 bg-gradient-to-t from-ink-900 via-ink-900/70 to-transparent" />
                    </div>
                  )}
                  {/* Thin inner highlight */}
                  <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-chrome-100/10 to-transparent" />
                  {/* Small red hover dot */}
                  <div className="absolute top-6 right-6 w-1.5 h-1.5 rounded-full bg-proform-red opacity-0 group-hover:opacity-100 transition-opacity duration-300" aria-hidden />

                  <div className="relative label mb-5">— 0{i + 1}</div>
                  <div className="relative font-display uppercase text-[26px] md:text-[30px] leading-[1.05] tracking-tight text-chrome-100">
                    {s.t}
                  </div>
                  <p className="relative mt-3 text-chrome-300 text-[15px] md:text-base leading-relaxed">
                    {s.desc}
                  </p>
                  <div className="relative mt-auto pt-6 flex items-end justify-between">
                    <span className="h-px w-8 bg-chrome-100/25 group-hover:w-14 group-hover:bg-proform-red transition-all duration-300" aria-hidden />
                    <span className="font-mono text-[11px] tracking-[0.24em] text-chrome-300 group-hover:text-proform-red group-hover:translate-x-1 transition-all duration-300">→</span>
                  </div>
                </Link>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery teaser */}
      <section data-testid={TID.home.galleryTeaser} className="relative py-24 md:py-32 border-t border-ink-700 overflow-hidden">
        <div className="wrap grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="label mb-4">— Chapter 05 · Archive</div>
            <h2 className="font-display uppercase text-4xl md:text-6xl leading-[0.95] tracking-tight">
              <MaskLineScroll>The work</MaskLineScroll>
              <MaskLineScroll delay={0.08}>is <span className="text-proform-red">coming</span>.</MaskLineScroll>
            </h2>
            <p className="mt-6 text-chrome-300 max-w-md">
              A full archive of collision repairs, custom paint, JDM builds, drift cars, race cars, and personal projects is being organized. Follow ProForm on Instagram for current work while the gallery is being assembled.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/gallery" className="btn">View Gallery →</Link>
              <a href={BRAND.instagramUrl} target="_blank" rel="noreferrer" className="btn btn-primary">Follow @{BRAND.instagramHandle}</a>
            </div>
          </div>
          <div className="md:col-span-7">
            {/* Editorial contact-sheet placeholder */}
            <div className="grid grid-cols-3 gap-2 md:gap-3">
              {[IMAGES.paintReflection, IMAGES.wheels, IMAGES.jdmSide, IMAGES.drift, IMAGES.aero, IMAGES.masking].map((s, i) => (
                <FadeUp key={i} delay={i * 0.04}>
                  <div className="clip-frame aspect-[3/4] grayscale hover:grayscale-0 transition-all duration-700">
                    <img src={s} alt="Archive contact sheet — temporary atmospheric imagery" loading="lazy" />
                    <div className="absolute top-2 left-2 label bg-ink-950/70 px-1.5 py-0.5">— 0{i + 1}</div>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Behind ProForm */}
      <section data-testid={TID.home.cody} className="relative py-24 md:py-32 border-t border-ink-700 bg-ink-900">
        <div className="wrap grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-6">
            <FadeUp>
              <div className="clip-frame aspect-[16/9] w-full">
                <img src={IMAGES.builder240} alt="Nissan 240SX at night on a Denver street" />
              </div>
            </FadeUp>
          </div>
          <div className="md:col-span-6">
            <div className="label mb-4">— Chapter 06 · Behind ProForm</div>
            <h2 className="font-display uppercase text-4xl md:text-6xl leading-[0.95] tracking-tight">
              <MaskLineScroll>Built by people who</MaskLineScroll>
              <MaskLineScroll delay={0.08}>live the <span className="text-proform-red">culture</span>.</MaskLineScroll>
            </h2>
            <p className="mt-6 text-chrome-300 leading-relaxed max-w-2xl">
              More than fifteen years of paint and body experience, with formal training across multiple paint applications and manufacturer product lines. ProForm is run by builders, enthusiasts, and drifters — with hands-on experience across imported, modified, rare, and performance vehicles.
            </p>
            <p className="mt-4 text-chrome-300 leading-relaxed max-w-2xl">
              That combination lets ProForm approach projects that don&apos;t fit the typical production body-shop model — while holding the same standards for everyday collision work.
            </p>
            <div className="mt-8 grid grid-cols-2 md:grid-cols-3 gap-4 max-w-2xl">
              {[
                ["15+", "Years experience"],
                ["Multi-brand", "Paint training"],
                ["Active", "Drift community"],
                ["JDM", "Imports"],
                ["Race", "Track builds"],
                ["Custom", "Personal projects"],
              ].map(([n, l]) => (
                <div key={l} className="border-t border-ink-700 pt-3">
                  <div className="font-display text-3xl leading-none">{n}</div>
                  <div className="label mt-1">{l}</div>
                </div>
              ))}
            </div>
            <Link to="/about" className="btn mt-10">More about ProForm →</Link>
          </div>
        </div>
      </section>

      {/* Partners */}
      <section data-testid={TID.home.partners} className="relative py-24 md:py-32 border-t border-ink-700">
        <div className="wrap">
          <div className="label mb-4">— Chapter 07 · Community</div>
          <h2 className="font-display uppercase text-4xl md:text-6xl leading-[0.95] tracking-tight max-w-4xl">
            <MaskLineScroll>Connected to Colorado&apos;s</MaskLineScroll>
            <MaskLineScroll delay={0.08}>performance <span className="text-proform-red">community</span>.</MaskLineScroll>
          </h2>
          <p className="mt-6 text-chrome-300 max-w-2xl">
            ProForm works alongside — and receives referrals through — respected automotive businesses across Denver&apos;s import, tuner, and motorsport network.
          </p>
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-3">
            {partners.map((p, i) => {
              const logo = PARTNER_LOGOS[p];
              return (
                <FadeUp key={p} delay={i * 0.05}>
                  <div
                    data-testid={`partner-card-${p.toLowerCase().replace(/\s+/g, "-")}`}
                    className="card p-6 md:p-8 h-full min-h-[220px] md:min-h-[240px] flex flex-col justify-between hover:border-proform-red transition-colors group"
                  >
                    <div className="label group-hover:text-proform-red transition-colors">— Partner · 0{i + 1}</div>
                    <div className="flex-1 flex items-center justify-center py-6">
                      {logo ? (
                        <img
                          src={logo}
                          alt={`${p} logo`}
                          loading="lazy"
                          className="max-h-16 md:max-h-20 w-auto object-contain opacity-90 group-hover:opacity-100 transition-opacity"
                        />
                      ) : (
                        <span className="font-mono text-[10px] tracking-[0.28em] text-chrome-300/60 uppercase">Logo pending</span>
                      )}
                    </div>
                    <div className="font-display text-lg md:text-xl uppercase tracking-tight text-center">{p}</div>
                  </div>
                </FadeUp>
              );
            })}
          </div>
          <div className="mt-6 label">Logos displayed with partner permission. Additional partners listed as text until permissions are supplied.</div>
        </div>
      </section>

      {/* Process */}
      <section data-testid={TID.home.process} className="relative py-24 md:py-32 border-t border-ink-700 bg-ink-900">
        <div className="wrap">
          <div className="label mb-4">— Chapter 08 · Process</div>
          <h2 className="font-display uppercase text-4xl md:text-6xl leading-[0.95] tracking-tight mb-16">
            <MaskLineScroll>From <span className="text-proform-red">first message</span></MaskLineScroll>
            <MaskLineScroll delay={0.08}>to final walk-around.</MaskLineScroll>
          </h2>
          <ol className="grid md:grid-cols-4 gap-4 md:gap-6">
            {process.map((p, i) => (
              <FadeUp key={p.n} delay={i * 0.05}>
                <li className="card p-6 md:p-8 h-full">
                  <div className="font-display text-6xl md:text-7xl text-proform-red leading-none">{p.n}</div>
                  <div className="mt-6 font-display uppercase text-xl tracking-tight">{p.t}</div>
                  <p className="mt-3 text-chrome-300 text-sm">{p.d}</p>
                </li>
              </FadeUp>
            ))}
          </ol>
          <p className="mt-8 label">We don&apos;t promise instant estimates from photos alone — nothing replaces an in-person inspection.</p>
        </div>
      </section>

      {/* Instagram teaser */}
      <section data-testid={TID.home.instagram} className="relative py-24 md:py-32 border-t border-ink-700">
        <div className="wrap grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-5">
            <div className="label mb-4">— Chapter 09 · Instagram</div>
            <h2 className="font-display uppercase text-4xl md:text-6xl leading-[0.95] tracking-tight">
              <MaskLineScroll>Current work,</MaskLineScroll>
              <MaskLineScroll delay={0.08}>in the <span className="text-proform-red">feed</span>.</MaskLineScroll>
            </h2>
            <p className="mt-6 text-chrome-300 max-w-md">
              While the archive is being assembled, follow @{BRAND.instagramHandle} for live progress from the shop, drift events, and personal builds.
            </p>
            <a href={BRAND.instagramUrl} target="_blank" rel="noreferrer" className="btn btn-primary mt-8">
              Follow @{BRAND.instagramHandle} →
            </a>
          </div>
          <div className="md:col-span-7 grid grid-cols-6 grid-rows-4 gap-2 md:gap-3 h-[70vh] md:h-[80vh]">
            <FadeUp className="col-span-4 row-span-3">
              <div className="clip-frame w-full h-full">
                <img src={IMAGES.drift} alt="Instagram-teaser · drift" loading="lazy" />
              </div>
            </FadeUp>
            <FadeUp delay={0.05} className="col-span-2 row-span-2">
              <div className="clip-frame w-full h-full">
                <img src={IMAGES.jdmSide} alt="Instagram-teaser · JDM" loading="lazy" />
              </div>
            </FadeUp>
            <FadeUp delay={0.1} className="col-span-2 row-span-2">
              <div className="clip-frame w-full h-full">
                <img src={IMAGES.paintBooth} alt="Instagram-teaser · paint booth" loading="lazy" />
              </div>
            </FadeUp>
            <FadeUp delay={0.15} className="col-span-4 row-span-1">
              <div className="clip-frame w-full h-full">
                <img src={IMAGES.paintReflection} alt="Instagram-teaser · finish" loading="lazy" />
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* Final CTA · FULL-BLEED */}
      <FullBleed
        image={IMAGES.finalCta}
        alt="Rear taillights and reflected neon on rainy pavement — temporary atmospheric imagery"
        side="center"
        scrim="edges"
        height="min-h-[92svh]"
        testid={TID.home.finalCta}
        redAccent
      >
        <div className="text-center mx-auto">
          <div className="label mb-4">— Chapter 10 · Estimate</div>
          <h2 className="font-display uppercase text-5xl md:text-8xl leading-[0.88] tracking-tight text-chrome-100 drop-shadow-[0_4px_20px_rgba(0,0,0,0.85)]">
            <MaskLineScroll>Your car isn&apos;t</MaskLineScroll>
            <MaskLineScroll delay={0.06}>ordinary.</MaskLineScroll>
            <MaskLineScroll delay={0.12}>Its <span className="text-proform-red">repair</span></MaskLineScroll>
            <MaskLineScroll delay={0.18}>shouldn&apos;t be either.</MaskLineScroll>
          </h2>
          <p className="mt-8 text-chrome-100/95 max-w-xl mx-auto drop-shadow-[0_2px_10px_rgba(0,0,0,0.85)]">
            Tell ProForm what happened, what you&apos;re building, or what ordinary shops have already told you they can&apos;t do.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link to="/estimate" className="btn btn-primary">Request an Estimate →</Link>
            <a href={`tel:${BRAND.phoneRaw}`} className="btn backdrop-blur-md bg-ink-950/40">Call {BRAND.phoneDisplay}</a>
            <a href={`sms:${BRAND.phoneRaw}`} className="btn backdrop-blur-md bg-ink-950/40">Text ProForm</a>
          </div>
        </div>
      </FullBleed>
    </>
  );
}
